import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useBreadcrumbsStore from "../../../stores/breadcrumbStore";
import { ConstantRoutes } from "../../../core/constantRoutes";
import { useWorkTimeStore } from "../../../stores/admin/workTimeStore";
import WorkTimeService from "../../../api/admin/workTimeService";
import { DayOfWeek } from "../../../core/enums/dayOfWeek";
import TimeZoneDisplay from "./timeZoneDisplay";
import { convertAdminWorkScheduleToUTC } from "../../../helpers/dateHelper";

const AdminWorkTime: React.FC = () => {
  const { t } = useTranslation();
  const {
    workTime,
    fetchWorkTime,
    updateHours,
    updateMinutes,
    updateSchedule,
  } = useWorkTimeStore();
  const { addBreadcrumb, clearBreadcrumbs } = useBreadcrumbsStore();

  useEffect(() => {
    clearBreadcrumbs();
    addBreadcrumb({ label: "Work Time", path: ConstantRoutes.adminWorkTime });
    fetchWorkTime();
  }, []);

  const handleTimeChange = (
    day: DayOfWeek,
    field: "start" | "end",
    value: string
  ) => {
    if (!workTime?.schedule) return;

    const updatedSchedule = workTime.schedule.map((item) => {
      if (item.day === day) {
        // Создаем обновленный элемент
        const updatedItem = {
          ...item,
          [field]: value,
        };

        // Если меняем start и он стал больше end
        if (field === "start" && value > updatedItem.end) {
          updatedItem.end = value; // Приравниваем end к новому start
        }
        // Если меняем end и он стал меньше start
        else if (field === "end" && value < updatedItem.start) {
          updatedItem.start = value; // Приравниваем start к новому end
        }

        return updatedItem;
      }
      return item;
    });

    updateSchedule(updatedSchedule);
  };

  const saveWorkTime = async () => {
    const workTimeService = new WorkTimeService();
    if (workTime !== undefined) {
      const updatedWorkTime = {
        hours: workTime.hours,
        minutes: workTime.minutes,
        schedule: convertAdminWorkScheduleToUTC(workTime.schedule),
      };
      await workTimeService.saveWorkTimes(updatedWorkTime);
      fetchWorkTime();
    }
  };

  const iAmHere = async () => {
    const workTimeService = new WorkTimeService();
    if (workTime !== undefined) {
      await workTimeService.imHere();
      fetchWorkTime();
    }
  };

  return (
    <div className="flex-1 flex flex-col p-2">
      <TimeZoneDisplay />

      <div className="flex flex-col md:flex-row gap-6">
        {/* Блок перерыва */}
        <div className="flex-1 p-4 border rounded-lg bg-content3">
          <h3 className="text-lg font-medium mb-4">{t("Break Time")}</h3>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-textsecondary mb-1">
                  {t("Hours")}
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 bg-content2 border border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t("Hours")}
                  value={workTime?.hours || 0}
                  onChange={(event) => updateHours(Number(event.target.value))}
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textsecondary mb-1">
                  {t("Minutes")}
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 bg-content2 border border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t("Minutes")}
                  value={workTime?.minutes || 0}
                  onChange={(event) =>
                    updateMinutes(Number(event.target.value))
                  }
                  min="0"
                  max="59"
                />
              </div>
            </div>

            <div className="flex flex-col justify-end gap-3">
              <button
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-opacity-90 
          transition duration-200 flex items-center justify-center gap-2"
                onClick={iAmHere}
              >
                <span>{t("I am here")}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Блок сохранения */}
        <div className="flex items-end pb-1">
          <button
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 
      transition duration-200 w-full md:w-auto font-medium"
            onClick={saveWorkTime}
          >
            {t("Save")}
          </button>
        </div>
      </div>

      {/* Расписание по дням недели */}
      <div className="w-full mb-6 mt-10">
        <h3 className="text-lg font-medium mb-4">{t("Weekly Schedule")}</h3>
        {workTime?.schedule?.map((daySchedule) => {
          const isDayOff = daySchedule.start === daySchedule.end;

          return (
            <div
              key={daySchedule.day}
              className={`mb-4 p-3 border rounded-lg ${
                isDayOff ? "bg-content4 border-red-200" : "border-primary"
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">{t(DayOfWeek[daySchedule.day])}</h4>
                {isDayOff && (
                  <span className="text-red-500 font-medium">выходной</span>
                )}
              </div>
              <div className="flex gap-4">
                <div className="w-1/2">
                  <label className="text-textsecondary">
                    {t("Start Time")}
                  </label>
                  <input
                    type="time"
                    className="flex-grow outline-none px-2 py-1 bg-content2 w-full border border-secondary rounded-xl"
                    value={daySchedule.start}
                    onChange={(e) =>
                      handleTimeChange(daySchedule.day, "start", e.target.value)
                    }
                    step="300"
                  />
                </div>
                <div className="w-1/2">
                  <label className="text-textsecondary">{t("End Time")}</label>
                  <input
                    type="time"
                    className="flex-grow outline-none px-2 py-1 bg-content2 w-full border border-secondary rounded-xl"
                    value={daySchedule.end}
                    onChange={(e) =>
                      handleTimeChange(daySchedule.day, "end", e.target.value)
                    }
                    step="300"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminWorkTime;
